import { Box, SxProps, TextField, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { onlyNumbers } from 'Regex';
import { regexFormController } from 'Utils/regexFormController';
import { useEffect, useState } from 'react';

interface IControlledTextField {
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  label?: string;
  disableLabelShrink?: boolean;
  name?: string;
  ariaLabel?: string;
  ariaDescribe?: string;
  ariaRequired?: boolean;
  defaultValue?: string;
  value: string;
  size?: 'small' | 'medium';
  autoFocus?: boolean;
  showError?: boolean;
  errorMsg?: string;
  errorMsgSx?: SxProps;
  sx?: SxProps;
  inputSx?: SxProps;
  labelSx?: SxProps;
  type?: string;
  setValue: Function;
  inputLimit?: number;
  inputRegexType?: 'NUMBER' | 'ID' | 'PHONE' | 'NAME' | 'EMAIL';
  error: string | null;
  onFocus?: () => void;
  onBlur?: () => void;
  setError: (error: string | null) => void;
  inputMode?:
    | 'text'
    | 'search'
    | 'email'
    | 'tel'
    | 'url'
    | 'none'
    | 'numeric'
    | 'decimal'
    | undefined;
  placeholder?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

export const ControlledTextField = ({
  onKeyDown,
  label,
  disableLabelShrink,
  name,
  ariaLabel,
  ariaDescribe,
  ariaRequired = false,
  defaultValue,
  value,
  setValue,
  size,
  autoFocus,
  showError,
  errorMsgSx,
  sx,
  inputSx,
  labelSx,
  type = 'text',
  inputLimit = Number.MAX_SAFE_INTEGER,
  inputRegexType,
  error,
  setError,
  inputMode,
  placeholder,
  startAdornment,
  endAdornment,
  onFocus = () => {},
  onBlur = undefined,
}: IControlledTextField) => {
  const [inputValue, setInputValue] = useState<string>(value);

  useEffect(() => {
    if (inputValue.length === 0) return;
    regexFormController(inputRegexType, inputValue, setError);
  }, [inputRegexType]);

  useEffect(() => {
    if (value.length === 0) setInputValue('');
  }, [value]);

  const onChangeDefaultHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setError(null);
    const val: string = event.target.value;
    switch (inputRegexType) {
      case 'ID': {
        if (onlyNumbers.test(val) && val.length <= inputLimit) {
          setInputValue(val);
          setValue(val);
        }
        break;
      }
      case 'PHONE': {
        if (onlyNumbers.test(val) && val.length <= inputLimit) {
          setInputValue(val);
          setValue(val);
        }
        break;
      }
      default: {
        if (val.length <= inputLimit) {
          setInputValue(val);
          setValue(val);
        }
      }
    }
  };

  const onBlurDefaultHandler = (event: React.FocusEvent<HTMLInputElement>) => {
    regexFormController(inputRegexType, event.target.value, setError);
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <TextField
        onFocus={onFocus}
        placeholder={placeholder}
        inputMode={inputMode}
        name={name}
        defaultValue={defaultValue}
        value={inputValue}
        label={label}
        autoFocus={autoFocus}
        error={error !== null && showError}
        size={size || 'small'}
        type={type}
        sx={{
          bgcolor: 'white',
          borderRadius: '8px',
          width: '100%',

          '& fieldset': {
            border: '1px solid',
            borderColor: 'primary.shade_200',
            borderRadius: '8px',
          },
          '& label.Mui-focused': {
            color: '#1525E8',
            bgcolor: 'white',
          },
          ...sx,
        }}
        onChange={onChangeDefaultHandler}
        onKeyDown={onKeyDown}
        onBlur={onBlurDefaultHandler}
        slotProps={{
          htmlInput: {
            'aria-describedby':
              error && showError
                ? `${name?.trim().replace(' ', '-')}-otp-form-error-msg`
                : ariaDescribe,
            'aria-required': ariaRequired,
            'aria-label': ariaLabel,
            sx: {
              '&:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px white inset',
              },
              ...inputSx,
            },
          },

          inputLabel: {
            shrink: disableLabelShrink ? false : undefined,
            sx: {
              fontSize: '16px',
              fontFamily: 'inherit',
              display:
                disableLabelShrink && inputValue.length > 0
                  ? 'none'
                  : undefined,
              ...labelSx,
            },
          },
          input: {
            startAdornment,
            endAdornment,
          },
        }}
      />
      {error && showError && (
        <Typography
          id={`${name?.trim().replace(' ', '-')}-otp-form-error-msg`}
          variant={'body6'}
          role='alert'
          sx={{
            color: 'error.main',
            margin: '0 0 0 7px',
            ...errorMsgSx,
          }}
        >
          {error || Dictionary[DICTIONARY.OTP_Login].requiredField}
        </Typography>
      )}
    </Box>
  );
};
